import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { uploadFile, openBlob, formatSize } from "@/lib/upload";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderOpen, Upload, Download, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const VIS = { admin: "Solo admin", tutti: "Tutti", operativo: "Squadre operative", ufficio: "Ufficio" };

function UploadForm({ folders, onDone }) {
  const [file, setFile] = useState(null);
  const [folder, setFolder] = useState("Generale");
  const [vis, setVis] = useState("admin");
  const [pct, setPct] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Scegli un file");
    try {
      setPct(0);
      const upload_id = await uploadFile(file, setPct);
      await api.post("/archive", { upload_id, folder, visibility: vis });
      toast.success("File caricato");
      setFile(null); e.target.reset(); onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setPct(null); }
  };
  return (
    <form onSubmit={send} className="grid sm:grid-cols-4 gap-3 bg-white border border-slate-200 rounded-2xl p-4" data-testid="archive-upload-form">
      <div className="sm:col-span-2"><Label>File (PDF, immagini, video, Word, Excel)</Label>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="archive-file-input" /></div>
      <div><Label>Cartella</Label>
        <Input list="archive-folders" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="archive-folder-input" />
        <datalist id="archive-folders">{folders.map((f) => <option key={f} value={f} />)}</datalist></div>
      <div><Label>Chi lo vede</Label>
        <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={vis} onChange={(e) => setVis(e.target.value)} data-testid="archive-visibility-select">
          {Object.entries(VIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select></div>
      <div className="sm:col-span-4 flex items-center gap-3">
        <Button type="submit" disabled={pct !== null} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="archive-upload-button">
          <Upload className="w-4 h-4 mr-2" /> {pct !== null ? `Caricamento ${pct}%` : "Carica"}
        </Button>
        {pct !== null && <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-sky-500 transition-[width]" style={{ width: `${pct}%` }} /></div>}
      </div>
    </form>
  );
}

export default function Archive() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [files, setFiles] = useState([]);
  const load = () => api.get("/archive").then((r) => setFiles(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  const groups = useMemo(() => files.reduce((g, f) => ({ ...g, [f.folder]: [...(g[f.folder] || []), f] }), {}), [files]);
  const remove = async (id) => {
    if (!window.confirm("Eliminare il file?")) return;
    try { await api.delete(`/archive/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeVis = async (id, visibility) => {
    try { await api.patch(`/archive/${id}`, { visibility }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="archive-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Documenti aziendali</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Archivio</h1>
      </header>
      {isAdmin && <UploadForm folders={Object.keys(groups)} onDone={load} />}
      {files.length === 0 ? <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">Nessun file.</div> : (
        Object.entries(groups).map(([folder, list]) => (
          <section key={folder} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <h2 className="px-4 py-3 bg-slate-50 font-display font-bold text-slate-900 flex items-center gap-2"><FolderOpen className="w-4 h-4 text-amber-500" /> {folder}</h2>
            <ul className="divide-y divide-slate-100">
              {list.map((f) => (
                <li key={f.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`archive-file-${f.id}`}>
                  <FileText className="w-5 h-5 text-sky-600" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{f.filename}</div>
                    <div className="text-xs text-slate-500">{formatSize(f.size)} · {f.created_at.slice(0, 10)}</div>
                  </div>
                  {isAdmin && (
                    <select className="h-8 rounded-md border border-slate-200 px-2 text-xs bg-white" value={f.visibility} onChange={(e) => changeVis(f.id, e.target.value)} data-testid={`archive-vis-${f.id}`}>
                      {Object.entries(VIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  )}
                  <button onClick={() => openBlob(`/archive/${f.id}/download`, f.filename).catch((e) => toast.error(formatApiError(e)))} className="p-2 text-slate-500 hover:text-sky-600" data-testid={`archive-download-${f.id}`}><Download className="w-4 h-4" /></button>
                  {isAdmin && <button onClick={() => remove(f.id)} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`archive-delete-${f.id}`}><Trash2 className="w-4 h-4" /></button>}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
